/**
 * Round 11 B3 -- the setup ribbon has now gone missing twice.
 *
 * The teaser renders nothing until `/v1/onboarding/status` answers,
 * because "is this bank fresh?" is unknowable before then. On a cold
 * load that leaves the administrator's own next-step control absent
 * from the screen he starts on: round 9 found it, round 10 recorded it
 * gone, round 11 found it back, and a twelve-load sweep did not
 * reproduce it, which makes it intermittent rather than fixed.
 *
 * A skeleton is not the answer on its own. The ribbon asserts
 * something -- "finish setting up" -- that is FALSE on a seasoned
 * bank, so rendering it hopefully would tell an administrator whose
 * setup is complete that it is not. What can be rendered immediately
 * is what this browser last SAW, refined the moment the answer lands.
 *
 * Deliberately small: the last answer's three numbers, keyed by
 * subject so two people sharing a machine never read each other's,
 * and every access guarded -- a browser with storage disabled must
 * degrade to today's behaviour, not to a crash. It is a render hint,
 * never a source of truth: the query still runs and still wins.
 */
export interface LastKnownSetup {
  show_getting_started: boolean;
  completed: number;
  total: number;
  /** Round 17 G20 -- the last verdict on THIS person's stations. The
   *  three numbers are the bank-wide ladder's, so while the journey was
   *  still loading a finished setup (7 of 7, nothing left) drew as a
   *  ladder rather than the hand-over. Optional: older entries lack it. */
  handing_over?: boolean;
}

const PREFIX = "marsad.setup.";

function keyFor(subject: string | undefined): string | null {
  return subject ? `${PREFIX}${subject}` : null;
}

export function readLastKnownSetup(subject: string | undefined): LastKnownSetup | null {
  const key = keyFor(subject);
  if (!key) return null;
  try {
    const raw = window.localStorage?.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const o = parsed as Record<string, unknown>;
    if (
      typeof o.show_getting_started !== "boolean" ||
      typeof o.completed !== "number" ||
      typeof o.total !== "number"
    ) {
      return null;
    }
    return {
      show_getting_started: o.show_getting_started,
      completed: o.completed,
      total: o.total,
      ...(typeof o.handing_over === "boolean" ? { handing_over: o.handing_over } : {}),
    };
  } catch {
    return null;
  }
}

export function writeLastKnownSetup(
  subject: string | undefined,
  value: LastKnownSetup,
): void {
  const key = keyFor(subject);
  if (!key) return;
  try {
    window.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage disabled or full -- the hint is optional by design */
  }
}
